import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, Lock, Minus, Plus, Sparkles, Trash2, X } from 'lucide-react';

const DEFAULT_LLM_TEST_CASE_FIELDS = [
  {
    key: 'test_description',
    label: 'Test Description',
    type: 'string',
    description: 'Clear objective of the test',
  },
  {
    key: 'User_Role',
    label: 'User Role',
    type: 'string',
    description: 'Persona performing the test',
  },
  {
    key: 'tab_flows',
    label: 'Tab Flows',
    type: 'string',
    description: 'Tabs or screens involved',
  },
  {
    key: 'test_steps',
    label: 'Test Steps',
    type: 'string_list',
    description: 'Automation-ready actions',
  },
  {
    key: 'expected_result',
    label: 'Expected Result',
    type: 'string',
    description: 'Observable success or failure outcome',
  },
  {
    key: 'user_inputs',
    label: 'User Inputs',
    type: 'user_inputs',
    description: 'Editable field and value pairs',
    required: true,
  },
  {
    key: 'user_input_sets',
    label: 'User Input Sets',
    type: 'user_input_sets',
    description: 'Curated automation data sets',
    required: true,
  },
];

const API_TEST_CASE_FIELD_CONFIG = {
  fields: [
    {
      key: 'test_description',
      label: 'Test Case Description',
      type: 'string',
      enabled: true,
      required: true,
      description: 'Concise API scenario description',
    },
    {
      key: 'User_Role',
      label: 'User Role',
      type: 'string',
      enabled: false,
      required: false,
      description: 'Persona performing the test',
    },
    {
      key: 'tab_flows',
      label: 'Tab Flows',
      type: 'string',
      enabled: false,
      required: false,
      description: 'Tabs or screens involved',
    },
    {
      key: 'test_steps',
      label: 'Test Steps',
      type: 'string_list',
      enabled: false,
      required: false,
      description: 'Manual steps are disabled for API testing',
    },
    {
      key: 'expected_result',
      label: 'Expected Result',
      type: 'string',
      enabled: false,
      required: false,
      description: 'Manual expected result is disabled for API testing',
    },
    {
      key: 'curl_command',
      label: 'cURL Request',
      type: 'string',
      enabled: true,
      required: true,
      description: 'Executable curl command for this API scenario',
    },
    {
      key: 'expected_api_response',
      label: 'Expected API Response',
      type: 'string',
      enabled: true,
      required: true,
      description: 'Expected status, response body, and response assertions',
    },
    {
      key: 'user_inputs',
      label: 'API Request Inputs',
      type: 'user_inputs',
      enabled: true,
      required: true,
      description: 'Editable API request values',
    },
    {
      key: 'user_input_sets',
      label: 'API Input Sets',
      type: 'user_input_sets',
      enabled: true,
      required: true,
      description: 'API request data sets',
    },
  ],
};

const MANDATORY_LLM_FIELD_KEYS = new Set(['user_inputs', 'user_input_sets']);
const FIELD_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const COUNT_PRESETS = [
  { key: 'lean', label: 'Lean', factor: 0.75, className: 'border-slate-200 bg-slate-100 text-slate-700' },
  { key: 'balanced', label: 'Balanced', factor: 1, className: 'border-blue-200 bg-blue-100 text-blue-700' },
  { key: 'thorough', label: 'Thorough', factor: 1.3, className: 'border-amber-200 bg-amber-100 text-amber-700' },
];

const normalizeFieldKey = (value) => String(value || '').trim();

const normalizeFieldRows = (config = null) => {
  const rawFields = Array.isArray(config?.fields) ? config.fields : null;
  const rawByKey = new Map(
    (rawFields || [])
      .map((field) => [String(field?.key || '').trim(), field])
      .filter(([key]) => key && key !== 'test_case_no')
  );

  const rows = DEFAULT_LLM_TEST_CASE_FIELDS.map((field) => {
    const raw = rawByKey.get(field.key) || {};
    const required = Boolean(field.required || raw.required || MANDATORY_LLM_FIELD_KEYS.has(field.key));
    return {
      ...field,
      ...raw,
      key: field.key,
      label: raw.label || field.label,
      description: raw.description || field.description,
      type: raw.type || field.type,
      required,
      enabled: required || (rawFields ? Boolean(raw.enabled) : true),
      custom: false,
    };
  });

  if (rawFields) {
    rawFields.forEach((field) => {
      const key = normalizeFieldKey(field?.key);
      if (!key || key === 'test_case_no' || DEFAULT_LLM_TEST_CASE_FIELDS.some((item) => item.key === key)) {
        return;
      }
      if (!FIELD_KEY_PATTERN.test(key)) return;
      rows.push({
        key,
        label: String(field?.label || key).trim() || key,
        description: String(field?.description || '').trim(),
        type: field?.type || 'string',
        required: Boolean(field?.required),
        enabled: field?.enabled !== false,
        custom: true,
      });
    });
  }

  return rows;
};

const serializeFieldRows = (rows) => ({
  fields: (rows || []).map((field) => ({
    key: field.key,
    label: field.label,
    description: field.description,
    type: field.type || 'string',
    enabled: Boolean(field.enabled || field.required),
    required: Boolean(field.required),
  })),
});

export const buildDefaultTestCaseFieldConfig = (config = null) => serializeFieldRows(normalizeFieldRows(config));

export const TestCasePlanningStep = ({
  planData,
  initialCounts,
  initialFocus,
  initialGlobalConstraints,
  initialTestCaseFieldConfig,
  status,
  onBack,
  onGenerate,
  showBackButton = true,
}) => {
  const plan = planData?.plan || {};
  const suggested = plan.suggested || {};
  const coverageMatrix = plan.coverage_matrix && typeof plan.coverage_matrix === 'object'
    ? plan.coverage_matrix
    : {};
  const coverageCategories = [
    ['Requirements', coverageMatrix.requirements],
    ['Roles', coverageMatrix.roles],
    ['Fields', coverageMatrix.fields],
    ['Validations', coverageMatrix.validations],
    ['States', coverageMatrix.states],
    ['Permissions', coverageMatrix.permissions],
    ['Integrations', coverageMatrix.integrations],
    ['Negative Paths', coverageMatrix.negative_paths],
    ['Boundary Cases', coverageMatrix.boundary_cases],
  ]
    .map(([label, items]) => ({
      label,
      items: Array.isArray(items) ? items.filter(Boolean) : [],
    }))
    .filter((category) => category.items.length > 0);
  const missingContext = Array.isArray(coverageMatrix.missing_context)
    ? coverageMatrix.missing_context.filter(Boolean)
    : [];
  const isApiTestingPlan = (
    plan?.planner_source === 'openapi_fallback'
    || plan?.signals?.source_type === 'openapi_operation'
    || Boolean(plan?.coverage_driven_counts?.api_positive_count)
    || /API Operation|OpenAPI|Swagger/i.test(String(planData?.section_title || ''))
  );
  const resolvedInitialTestCaseFieldConfig = initialTestCaseFieldConfig || (isApiTestingPlan ? API_TEST_CASE_FIELD_CONFIG : null);
  const backendMaxCap = Number(plan.max_cap || 0);
  const maxCap = backendMaxCap > 0 ? backendMaxCap : null;
  const [positiveCount, setPositiveCount] = useState(
    Number(initialCounts?.positive_count ?? suggested.positive_count ?? 20)
  );
  const [negativeCount, setNegativeCount] = useState(
    Number(initialCounts?.negative_count ?? suggested.negative_count ?? 12)
  );
  const [focusItems, setFocusItems] = useState(
    Array.isArray(initialFocus) && initialFocus.length > 0
      ? initialFocus
      : Array.isArray(plan.coverage_focus)
        ? plan.coverage_focus
        : []
  );
  const [newFocus, setNewFocus] = useState('');
  const [globalConstraints, setGlobalConstraints] = useState(
    Array.isArray(initialGlobalConstraints) && initialGlobalConstraints.length > 0
      ? initialGlobalConstraints
      : Array.isArray(planData?.approved_global_constraints)
        ? planData.approved_global_constraints
        : Array.isArray(plan?.approved_global_constraints)
          ? plan.approved_global_constraints
          : []
  );
  const [newGlobalConstraint, setNewGlobalConstraint] = useState('');
  const [fieldRows, setFieldRows] = useState(() => normalizeFieldRows(resolvedInitialTestCaseFieldConfig));
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [fieldConfigError, setFieldConfigError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const suggestedPositive = Number(suggested.positive_count || 0);
  const suggestedNegative = Number(suggested.negative_count || 0);
  const suggestedTotal = Math.max(1, suggestedPositive + suggestedNegative);
  const countReasoning =
    plan.count_reasoning && typeof plan.count_reasoning === 'object'
      ? plan.count_reasoning
      : {};
  const positiveReason =
    typeof countReasoning.positive === 'string' ? countReasoning.positive.trim() : '';
  const negativeReason =
    typeof countReasoning.negative === 'string' ? countReasoning.negative.trim() : '';
  const reasonClampStyle = {
    display: '-webkit-box',
    WebkitLineClamp: 5,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
  const clampCount = (value) => {
    const rounded = Math.max(1, Math.round(Number(value) || 1));
    return maxCap ? Math.min(maxCap, rounded) : rounded;
  };
  const cappedSuggestedPositive = clampCount(suggestedPositive || 1);
  const cappedSuggestedNegative = clampCount(suggestedNegative || 1);
  const cappedSuggestedTotal = cappedSuggestedPositive + cappedSuggestedNegative;
  const isSuggestedOverLimit =
    Boolean(maxCap && (suggestedPositive > maxCap || suggestedNegative > maxCap || suggestedTotal > maxCap * 2));

  const safeCounts = {
    positive: clampCount(positiveCount),
    negative: clampCount(negativeCount),
  };
  const presetCountKey = (counts) => `${counts.positive}:${counts.negative}`;
  const presetOptions = COUNT_PRESETS.map((preset) => ({
    ...preset,
    target: {
      positive: clampCount((suggestedPositive || 1) * preset.factor),
      negative: clampCount((suggestedNegative || 1) * preset.factor),
    },
  }));
  const preferredPresetByTarget = presetOptions.reduce((acc, preset) => {
    const key = presetCountKey(preset.target);
    const current = acc.get(key);
    if (!current || preset.key === 'balanced') {
      acc.set(key, preset);
    }
    return acc;
  }, new Map());
  const presetOptionsWithAvailability = presetOptions.map((preset) => {
    const preferredPreset = preferredPresetByTarget.get(presetCountKey(preset.target));
    const isDuplicate = preferredPreset?.key !== preset.key;
    return {
      ...preset,
      disabledReason: isDuplicate
        ? `${preset.label} matches ${preferredPreset?.label || 'another preset'} at the current limit.`
        : '',
      isDuplicate,
    };
  });
  const findPresetKeyForCounts = (counts) => {
    const matchingPreset = presetOptionsWithAvailability.find(
      (preset) =>
        !preset.isDuplicate &&
        preset.target.positive === counts.positive &&
        preset.target.negative === counts.negative
    );
    return matchingPreset?.key || null;
  };
  const [activePresetKey, setActivePresetKey] = useState(() => {
    const initialPositive = Number(initialCounts?.positive_count ?? suggested.positive_count ?? 20);
    const initialNegative = Number(initialCounts?.negative_count ?? suggested.negative_count ?? 12);
    return findPresetKeyForCounts({
      positive: clampCount(initialPositive),
      negative: clampCount(initialNegative),
    });
  });
  const isGenerating = status === 'processing' || status === 'bulk_processing' || isSubmitting;

  const totalPlanned = safeCounts.positive + safeCounts.negative;
  const deltaFromSuggested = totalPlanned - (isSuggestedOverLimit ? cappedSuggestedTotal : suggestedTotal);

  const applyPreset = (preset) => {
    if (preset.isDuplicate) return;
    setPositiveCount(preset.target.positive);
    setNegativeCount(preset.target.negative);
    setActivePresetKey(preset.key);
  };

  const updatePositiveCount = (value) => {
    setActivePresetKey(null);
    setPositiveCount(value);
  };

  const updateNegativeCount = (value) => {
    setActivePresetKey(null);
    setNegativeCount(value);
  };

  const addFocus = () => {
    const val = (newFocus || '').trim();
    if (!val) return;
    const exists = focusItems.some((item) => item.toLowerCase() === val.toLowerCase());
    if (!exists) setFocusItems((prev) => [...prev, val]);
    setNewFocus('');
  };

  const removeFocus = (item) => {
    setFocusItems((prev) => prev.filter((entry) => entry !== item));
  };

  const addGlobalConstraint = () => {
    const value = (newGlobalConstraint || '').trim();
    if (!value) return;
    const exists = globalConstraints.some(
      (item) => item.toLowerCase() === value.toLowerCase()
    );
    if (!exists) {
      setGlobalConstraints((prev) => [...prev, value]);
    }
    setNewGlobalConstraint('');
  };

  const removeGlobalConstraint = (item) => {
    setGlobalConstraints((prev) => prev.filter((entry) => entry !== item));
  };

  useEffect(() => {
    if (status !== 'processing') {
      setIsSubmitting(false);
    }
  }, [status]);

  useEffect(() => {
    setFieldRows(normalizeFieldRows(resolvedInitialTestCaseFieldConfig));
  }, [resolvedInitialTestCaseFieldConfig]);

  const toggleField = (key) => {
    setFieldConfigError('');
    setFieldRows((prev) =>
      prev.map((field) => {
        if (field.key !== key || field.required) return field;
        return { ...field, enabled: !field.enabled };
      })
    );
  };

  const removeCustomField = (key) => {
    setFieldConfigError('');
    setFieldRows((prev) => prev.filter((field) => field.key !== key || !field.custom));
  };

  const addCustomField = () => {
    const key = normalizeFieldKey(newFieldKey);
    if (!FIELD_KEY_PATTERN.test(key)) {
      setFieldConfigError('Use a key like priority or business_rule_ref.');
      return;
    }
    if (key === 'test_case_no' || fieldRows.some((field) => field.key.toLowerCase() === key.toLowerCase())) {
      setFieldConfigError('That key already exists.');
      return;
    }
    setFieldRows((prev) => [
      ...prev,
      {
        key,
        label: String(newFieldLabel || key).trim() || key,
        description: String(newFieldDescription || '').trim(),
        type: 'string',
        enabled: true,
        required: false,
        custom: true,
      },
    ]);
    setNewFieldKey('');
    setNewFieldLabel('');
    setNewFieldDescription('');
    setFieldConfigError('');
  };

  const selectedFieldKeys = fieldRows
    .filter((field) => field.enabled || field.required)
    .map((field) => field.key);
  const automationFieldWarning = !isApiTestingPlan && !selectedFieldKeys.includes('test_steps');

  const submitGeneration = () => {
    setIsSubmitting(true);
    onGenerate({
      positive_count: safeCounts.positive,
      negative_count: safeCounts.negative,
      additional_focus: focusItems,
      global_constraints: globalConstraints,
      test_case_field_config: serializeFieldRows(fieldRows),
    });
  };

  return (
    <div className="space-y-5">
      <section className="soft-card border-indigo-200/70 bg-gradient-to-br from-indigo-50 via-blue-50 to-white p-4 md:p-5">
        <div>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-700">
                Pre-Analysis Plan
              </p>
              <h3 className="mt-1 text-base font-bold text-slate-900 md:text-lg">
                {planData?.section_title || 'Selected Section'}
              </h3>
              <p className="mt-2 whitespace-pre-wrap text-[13px] leading-5 text-slate-700">
                {plan?.section_overview || 'Use this plan to calibrate coverage before generation.'}
              </p>
            </div>
            <div className="w-full max-w-sm rounded-xl border border-white/80 bg-white/90 px-3 py-2.5 md:ml-4 md:w-[260px] md:flex-shrink-0">
              <p className="text-[11px] text-slate-500">Suggested Counts</p>
              <p className="mt-1 text-sm font-bold text-slate-900">
                {suggestedPositive} positive / {suggestedNegative} negative
              </p>
              <p className={`mt-1 text-[11px] ${isSuggestedOverLimit ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
                {maxCap
                  ? `Max ${maxCap * 2} total: ${maxCap} positive and ${maxCap} negative.`
                  : 'No fixed count cap; use coverage needs to set the run size.'}
              </p>
            </div>
          </div>
          <div className="mt-3 rounded-xl border border-indigo-100 bg-white/90 p-2.5 md:p-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-indigo-700">
              Count Justification
            </p>
            <div className="mt-2 grid gap-2.5 lg:grid-cols-2">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700">
                  Why {suggestedPositive} positive?
                </p>
                <p className="mt-1 text-[12px] leading-5 text-slate-700" style={reasonClampStyle}>
                  {positiveReason || 'No LLM reason returned for positive count. Re-run pre-analysis.'}
                </p>
              </div>
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700">
                  Why {suggestedNegative} negative?
                </p>
                <p className="mt-1 text-[12px] leading-5 text-slate-700" style={reasonClampStyle}>
                  {negativeReason || 'No LLM reason returned for negative count. Re-run pre-analysis.'}
                </p>
              </div>
            </div>
          </div>
          {(plan.coverage_summary || coverageCategories.length > 0 || missingContext.length > 0) && (
            <div className="mt-3 rounded-xl border border-indigo-100 bg-white/90 p-2.5 md:p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-indigo-700">
                Coverage Matrix
              </p>
              {plan.coverage_summary && (
                <p className="mt-1 text-[12px] leading-5 text-slate-700">
                  {plan.coverage_summary}
                </p>
              )}
              {coverageCategories.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {coverageCategories.map((category) => (
                    <div key={category.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                        {category.label}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {category.items.length}
                      </p>
                      <p className="mt-1 truncate text-[11px] text-slate-500">
                        {category.items.slice(0, 3).join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {missingContext.length > 0 && (
                <p className="mt-2 text-xs text-amber-700">
                  Missing context: {missingContext.slice(0, 3).join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="mt-2 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="soft-card h-full space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Quick Presets</p>
            <p className="mt-1 text-xs text-slate-600">
              Apply a preset and adjust manually if needed.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {presetOptionsWithAvailability.map((preset) => {
              const isActive = activePresetKey === preset.key;
              return (
                <button
                  key={preset.key}
                  type="button"
                  disabled={preset.isDuplicate}
                  title={preset.disabledReason || undefined}
                  onClick={() => applyPreset(preset)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${preset.className} ${
                    isActive ? 'ring-2 ring-offset-1 ring-indigo-300' : ''
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-500">Planned Total</p>
            <p className="mt-1 text-base font-semibold text-slate-900">{totalPlanned} test cases</p>
            <p className="mt-1 text-xs text-slate-600">
              {isSuggestedOverLimit
                ? `Original suggestion was ${suggestedTotal}; capped plan limit is ${cappedSuggestedTotal}.`
                : deltaFromSuggested === 0
                ? 'Aligned with suggested total.'
                : `${deltaFromSuggested > 0 ? '+' : ''}${deltaFromSuggested} vs suggested total (${suggestedTotal}).`}
            </p>
            {isSuggestedOverLimit ? (
              <p className="mt-1 text-xs font-medium text-amber-700">
                You can generate up to {maxCap} positive and {maxCap} negative test cases in one run.
              </p>
            ) : null}
          </div>
        </div>

        <div className="soft-card h-full space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Coverage Distribution</p>
            <p className="mt-1 text-xs text-slate-600">
              Fine-tune positive and negative mix for this run.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-stretch">
            {[
              ['Positive Test Cases', safeCounts.positive, updatePositiveCount],
              ['Negative Test Cases', safeCounts.negative, updateNegativeCount],
            ].map(([label, value, setter]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">{label}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setter(Math.max(1, value - 1))}
                    className="secondary-btn h-10 w-10 px-0 py-0"
                    type="button"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={maxCap || undefined}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className="text-input text-center font-semibold"
                  />
                  <button
                    onClick={() => setter(maxCap ? Math.min(maxCap, value + 1) : value + 1)}
                    className="secondary-btn h-10 w-10 px-0 py-0"
                    type="button"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="soft-card space-y-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Coverage Focus</p>
          <p className="mt-1 text-xs text-slate-600">
            Add or remove focus areas to guide generation for this module.
          </p>
        </div>
        {focusItems.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {focusItems.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs text-indigo-800"
              >
                {item}
                <button
                  type="button"
                  onClick={() => removeFocus(item)}
                  className="text-indigo-700 hover:text-indigo-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            No custom focus added yet. Use the field below to add one.
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={newFocus}
            onChange={(e) => setNewFocus(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addFocus();
              }
            }}
            placeholder="Add a focus area (e.g., consent retries, edge validations)"
            className="text-input flex-1"
          />
          <button type="button" onClick={addFocus} className="secondary-btn px-4">
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </section>

      <section className="soft-card space-y-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Global Constraints</p>
          <p className="mt-1 text-xs text-slate-600">
            These apply to every generated test case for this section.
          </p>
        </div>
        {globalConstraints.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {globalConstraints.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs text-emerald-800"
              >
                {item}
                <button
                  type="button"
                  onClick={() => removeGlobalConstraint(item)}
                  className="text-emerald-700 hover:text-emerald-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            No global constraints added yet.
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={newGlobalConstraint}
            onChange={(e) => setNewGlobalConstraint(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addGlobalConstraint();
              }
            }}
            placeholder="Add a global constraint (e.g., mask PII in all outputs)"
            className="text-input flex-1"
          />
          <button type="button" onClick={addGlobalConstraint} className="secondary-btn px-4">
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </section>

      <section className="soft-card space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {isApiTestingPlan ? 'API Output Fields' : 'LLM Output Fields'}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {isApiTestingPlan
                ? 'Swagger/API cases return a description, executable curl, and expected API response.'
                : 'Choose which fields Claude should return for each generated test case.'}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
            {selectedFieldKeys.length + 1} final keys
          </div>
        </div>

        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {fieldRows.map((field) => {
            const checked = Boolean(field.enabled || field.required);
            return (
              <div
                key={field.key}
                className={`rounded-lg border px-3 py-2.5 ${
                  checked ? 'border-indigo-200 bg-indigo-50/70' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <label className="flex min-w-0 cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={field.required}
                      onChange={() => toggleField(field.key)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-slate-900">
                        {field.label || field.key}
                      </span>
                      <span className="mt-0.5 block break-words text-[11px] text-slate-500">
                        {field.key}
                      </span>
                    </span>
                  </label>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    {field.required && (
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-indigo-200 bg-white text-indigo-700">
                        <Lock className="h-3.5 w-3.5" />
                      </span>
                    )}
                    {field.custom && (
                      <button
                        type="button"
                        onClick={() => removeCustomField(field.key)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                {field.description && (
                  <p className="mt-2 text-xs leading-5 text-slate-600">{field.description}</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
            Add Custom Field
          </p>
          <div className="mt-3 grid gap-2 lg:grid-cols-[0.7fr_0.8fr_1.5fr_auto]">
            <input
              type="text"
              value={newFieldKey}
              onChange={(e) => setNewFieldKey(e.target.value)}
              placeholder="key"
              className="text-input"
            />
            <input
              type="text"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              placeholder="Label"
              className="text-input"
            />
            <input
              type="text"
              value={newFieldDescription}
              onChange={(e) => setNewFieldDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomField();
                }
              }}
              placeholder="Description for Claude"
              className="text-input"
            />
            <button type="button" onClick={addCustomField} className="secondary-btn px-4">
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
          {fieldConfigError && (
            <p className="mt-2 text-xs font-medium text-rose-700">{fieldConfigError}</p>
          )}
          {automationFieldWarning && (
            <p className="mt-2 text-xs text-amber-700">
              Removing test steps can reduce automation quality.
            </p>
          )}
        </div>
      </section>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 text-amber-700" />
        <p className="text-xs text-amber-800">
          Start from suggested counts and adjust based on risk concentration. {maxCap
            ? `Maximum is ${maxCap * 2} total test cases, with ${maxCap} per type.`
            : 'There is no fixed cap for this plan.'}
        </p>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        {showBackButton && onBack ? (
          <button onClick={onBack} className="secondary-btn" type="button" disabled={isGenerating}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        ) : <span />}
        <button
          onClick={submitGeneration}
          disabled={isGenerating}
          className="primary-btn bg-gradient-to-r from-indigo-600 to-blue-600 disabled:opacity-50"
          type="button"
          aria-busy={isGenerating}
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating Test Cases...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate with Approved Counts
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
